import React from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { poolwerxColors } from '../../../theme/colors'

export interface AppDrawerHeaderProps {
  eyebrow?: string
  /**
   * Eyebrow colour. Defaults to the neutral tertiary grey. A caller can darken
   * it (e.g. the exception drawer passes ``text.secondary``) without shifting
   * the eyebrow on every other app drawer.
   */
  eyebrowColor?: string
  title: React.ReactNode
  titleAttr?: string
  /**
   * Title colour. Defaults to the neutral primary text. The exception drawer
   * opts into teal as an identity cue; other surfaces (e.g. the feedback
   * drawer) leave it neutral so teal stays reserved for links / the active tab.
   */
  titleColor?: string
  subtitle?: React.ReactNode
  /** Optional slot rendered directly below the subtitle (e.g. a source-link
   * row). Kept generic so this shared chrome stays decoupled from any one
   * caller's payload. */
  belowSubtitle?: React.ReactNode
  onClose: () => void
  sx?: SxProps<Theme>
}

const AppDrawerHeader: React.FC<AppDrawerHeaderProps> = ({
  eyebrow,
  eyebrowColor = poolwerxColors.text.tertiary,
  title,
  titleAttr,
  titleColor = poolwerxColors.text.primary,
  subtitle,
  belowSubtitle,
  onClose,
  sx,
}) => (
  <Box
    sx={{
      // Tight vertical rhythm so the header stays compact on short viewports
      // and sits close to the tab row below (a single hairline divider, no
      // large gap). eyebrow -> title -> meta steps are deliberately small.
      pt: '14px',
      px: '22px',
      pb: '10px',
      bgcolor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      ...sx,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      {eyebrow ? (
        <Typography
          variant="overline"
          sx={{
            fontSize: '11px',
            letterSpacing: '0.09em',
            fontWeight: 600,
            color: eyebrowColor,
            lineHeight: 1.2,
          }}
        >
          {eyebrow}
        </Typography>
      ) : (
        <Box />
      )}
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
    <Typography
      variant="h6"
      title={titleAttr}
      sx={{
        fontWeight: 600,
        fontSize: '20px',
        lineHeight: 1.25,
        color: titleColor,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        mt: eyebrow ? '4px' : 0,
      }}
    >
      {title}
    </Typography>
    {subtitle != null &&
      (typeof subtitle === 'string' ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: '2px',
            fontSize: '13px',
            lineHeight: 1.3,
            color: poolwerxColors.text.secondary,
          }}
        >
          {subtitle}
        </Typography>
      ) : (
        <Box sx={{ mt: '2px' }}>{subtitle}</Box>
      ))}
    {belowSubtitle}
  </Box>
)

export default AppDrawerHeader
